import { createContext, useContext } from "react";
import type { UseQueryResult } from "@tanstack/react-query";
import type { ObsDraftData } from "@/features/obs/lib/draft-store";
import type { TileFiles, TileLocale } from "@/features/obs/lib/tiles";
import type { StepGates, WizardStep } from "@/features/obs/lib/wizard-steps";
import type {
  ObsChartOptions,
  ObsReport,
  ObsRole,
  ObsRoleProposal,
  ObsSheetProfile,
  ObsUploadResult,
} from "@/types/obs";

/**
 * Everything the create-a-report steps share.
 *
 * The steps are separate routes now, so they cannot be handed props by a common
 * parent the way a modal's sections were — and the draft has to outlive each of
 * them, since going back to a step must find it exactly as it was left. One
 * context on the wizard's layout route owns the whole draft; a step is a form
 * over part of it and holds no state of its own.
 */
export interface ObsWizardValue {
  /** False until the stored draft AND its files have been read back. */
  hydrated: boolean;
  /** A draft with real content was restored, and the operator has not been told yet. */
  resumed: boolean;
  dismissResumeNotice: () => void;
  /** Throw the draft away and begin again — routed through the discard question when the report already exists. */
  startOver: () => void;

  step: WizardStep;
  goTo: (step: WizardStep) => void;
  gates: StepGates;

  draft: ObsDraftData;
  patch: (partial: Partial<ObsDraftData>) => void;

  /** The picked files themselves. Restored from IndexedDB; never re-injectable into a file input. */
  workbook: File | null;
  tiles: TileFiles;
  pickWorkbook: (file: File) => void;
  pickTile: (locale: TileLocale, file: File | null) => void;
  /**
   * The draft remembers a workbook the browser could not hand back, AND it was
   * never successfully uploaded — so the flow genuinely cannot continue until it
   * is picked again. False once the figures are on the server: from that point
   * the file has done its job and the wizard needs it no more.
   */
  needsWorkbookAgain: boolean;

  /** The sheet currently chosen out of the profiled workbook. */
  sheet: ObsSheetProfile | null;
  pickSheet: (name: string) => void;
  proposalFor: (role: ObsRole) => ObsRoleProposal | null;

  slugValid: boolean;
  /**
   * The report that already owns this slug, or null.
   *
   * Answered on step ONE. The server refuses a duplicate slug too, but only at
   * the create call on the units step — and a slug cannot be patched, so a
   * collision found there costs the operator the whole wizard. Null while the
   * slug is malformed (that has its own message) and once this draft has created
   * its own report, whose slug is in the list legitimately.
   */
  slugConflict: ObsReport | null;
  /**
   * Where the availability answer stands. `unknown` is a real outcome and not a
   * synonym for free: the list may have failed, and a slug must not be reported
   * as available on the strength of a request that never succeeded.
   *
   * `idle` covers the cases with nothing to ask — an empty or malformed slug
   * (which has its own message) and a report this draft has already created.
   */
  slugCheck: SlugCheck;
  unitsDone: boolean;
  /** Any request is in flight — every forward action is disabled while true. */
  busy: boolean;
  profiling: boolean;
  saving: boolean;

  chartOptions: UseQueryResult<ObsChartOptions, Error>;

  /** Set once the report is live on the site; past this the wizard is a receipt. */
  published: ObsUploadResult | null;
  createAndUpload: () => void;
  saveCharts: () => void;
  publish: () => void;

  /** Leaving with a created-but-unpublished report behind asks first. */
  discarding: boolean;
  closeDiscardPrompt: () => void;
  requestExit: () => void;
  keepDraft: () => void;
  discardDraft: () => void;
  removing: boolean;
}

export type SlugCheck = "idle" | "checking" | "free" | "taken" | "unknown";

export const ObsWizardContext = createContext<ObsWizardValue | null>(null);

export function useObsWizard(): ObsWizardValue {
  const value = useContext(ObsWizardContext);
  if (!value) {
    throw new Error("useObsWizard must be used inside <ObsWizardProvider>");
  }
  return value;
}
